import { DetailsRow, IColumn, IconButton, IDetailsRowProps } from '@fluentui/react'
import * as React from 'react'
import { FunctionComponent, ReactElement } from 'react'
import { formatShortDate } from '../../../helpers/dateHelper'
import MoneyText from '../../Text/Currency/MoneyText'
import { RegistrationOverviewDto } from '../../../config/registrationConfig'
import StealthLink from '../../../components/StealthLink'
import DialogButton from '../../../components/Button/DialogButton'
import DeleteDialog, { DeleteDialogProps } from '../../../components/Dialog/LegacyDeleteDialog'
import { RegistrationItemProps } from './RegistrationList'
import { Destination } from '../../../context/AxiosContext'
import PersonAvatar from '../../PersonAvatar'

export type ViewRegistrationRowProps<D extends RegistrationOverviewDto, I = {}> = {
    listProps: RegistrationItemProps<I>
    rowProps: Omit<IDetailsRowProps, 'item'> & { item: D }
    onDelete: () => void
}

export type ViewRegistrationColumnProps<D extends RegistrationOverviewDto, I = {}> = {
    item?: D
    column?: IColumn
} & Pick<Props<D, I>, 'onDelete'> &
    RegistrationItemProps<I>

type Props<D extends RegistrationOverviewDto, I> = ViewRegistrationRowProps<D, I> & {
    columns: FunctionComponent<ViewRegistrationColumnProps<D, I>>
}

export function ViewRegistrationColumns<D extends RegistrationOverviewDto, I = {}>(
    props: ViewRegistrationColumnProps<D, I>,
): ReactElement {
    switch (props.column.key) {
        case 'user':
            return (
                <PersonAvatar
                    key={`persona-${props.item.id}`}
                    name={props.item.user.fullName}
                    color={'colorful'}
                    size={24}
                />
            )
        case 'date':
            return <>{formatShortDate(props.item.date)}</>
        case 'rate':
            return <MoneyText value={props.item.rate} />
        case 'invoice':
            return props.item.invoice ? (
                <StealthLink id={props.item.invoice.pdfId} destination={Destination.Documents}>
                    {props.item.invoice.number || <em>Onbekend</em>}
                </StealthLink>
            ) : (
                <></>
            )
        case 'actions':
            return props.item.invoice ? (
                <></>
            ) : (
                <DialogButton<DeleteDialogProps>
                    button={IconButton}
                    buttonProps={{ iconProps: { iconName: 'Delete' } }}
                    dialog={DeleteDialog}
                    dialogProps={{ onDelete: props.onDelete }}
                />
            )
    }

    return <>{props.item[props.column.fieldName || props.column.key]}</>
}

export default function ViewRegistrationRow<D extends RegistrationOverviewDto, I = {}>(
    props: Props<D, I>,
): ReactElement {
    return (
        <DetailsRow
            {...props.rowProps}
            onRenderItemColumn={(item?: D, _index?: number, column?: IColumn) => (
                <props.columns {...props.listProps} item={item} column={column} onDelete={props.onDelete} />
            )}
        />
    )
}
